import { whitelineApi } from '@/shared/api';

export interface GetClientFinanceSummaryRequest {
  orgId: string;
}

export type FinanceBadge = 'EXPECTED' | 'NONE' | 'OVERDUE';

export interface NearestPayment {
  amount: number;
  date: string;
}

export interface GetClientFinanceSummaryResponse {
  availableBalance: number;
  badge: FinanceBadge;
  currency: string | null;
  nearestPayment: NearestPayment | null;
  totalToPay: number;
}

export interface GetClientPaymentsRequest {
  activeOnly?: boolean;
  from?: string;
  includeRemoved?: boolean;
  orgId: string;
  to?: string;
  type?: PaymentHistoryType;
}

export interface GetClientPaymentsResponse {
  groups: PaymentHistoryGroup[] | null;
  totalCount: number;
}

export interface GetClientUpcomingPaymentsRequest {
  orgId: string;
}

export interface GetClientUpcomingPaymentsResponse {
  allCovered: boolean;
  counts: UpcomingPaymentsCounts;
  groups: UpcomingPaymentsShipmentGroup[] | null;
}

export interface UpcomingPaymentItem {
  amount: number;
  category: UpcomingPaymentCategory;
  coveredAmount: number;
  date: string;
  dateIsEstimated: boolean;
  description: string | null;
  document: string | null;
  id: string;
  status: UpcomingPaymentStatus;
  title: string | null;
  totalAmount: number;
  urgency: UpcomingPaymentUrgency;
}

export interface PaymentHistoryGroup {
  budget: number | null;
  items: PaymentHistoryItem[] | null;
  shipmentId: string | null;
  shipmentNumber: string | null;
  spent: number;
}

export interface PaymentHistoryItem {
  amount: number;
  category: string | null;
  date: string;
  document: string | null;
  id: string;
  removedAt: string | null;
  title: string | null;
}

export type PaymentHistoryType = 'expense' | 'income';

export type UpcomingPaymentStatus = 'EXPECTED' | 'OVERDUE';

export type UpcomingPaymentCategory = 'ADVANCE' | 'CUSTOMS' | 'FINAL' | 'GOODS_ADVANCE' | 'GOODS_FINAL' | 'SURCHARGE';

export type UpcomingPaymentUrgency = 'NORMAL' | 'RED' | 'YELLOW';

export interface UpcomingPaymentsCounts {
  all: number;
  expected: number;
  overdue: number;
}

export interface UpcomingPaymentsShipmentGroup {
  items: UpcomingPaymentItem[] | null;
  shipmentId: string | null;
  shipmentNumber: string | null;
}

export const getClientFinanceSummary = async (
  payload: GetClientFinanceSummaryRequest
): Promise<GetClientFinanceSummaryResponse> => {
  const response = await whitelineApi.get<GetClientFinanceSummaryResponse>(
    `/api/clients/${payload.orgId}/finance/summary`
  );

  return response.data;
};

export const getClientPayments = async (
  payload: GetClientPaymentsRequest
): Promise<GetClientPaymentsResponse> => {
  const params: Record<string, boolean | string> = {};

  if (payload.activeOnly !== undefined) {
    params.active_only = payload.activeOnly;
  }

  if (payload.from) {
    params.from = payload.from;
  }

  if (payload.includeRemoved !== undefined) {
    params.include_removed = payload.includeRemoved;
  }

  if (payload.to) {
    params.to = payload.to;
  }

  if (payload.type) {
    params.type = payload.type;
  }

  const url = `/api/clients/${payload.orgId}/payments`;
  const response = Object.keys(params).length > 0
    ? await whitelineApi.get<GetClientPaymentsResponse>(url, { params })
    : await whitelineApi.get<GetClientPaymentsResponse>(url);

  return response.data;
};

export const getClientUpcomingPayments = async (
  payload: GetClientUpcomingPaymentsRequest
): Promise<GetClientUpcomingPaymentsResponse> => {
  const response = await whitelineApi.get<GetClientUpcomingPaymentsResponse>(
    `/api/clients/${payload.orgId}/finance/upcoming`
  );

  return response.data;
};